import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, SpaceBetween, Spinner } from '@cloudscape-design/components';
import PhoneInput from 'react-phone-input-2';

import { TextInput } from '../../components/TextInput';
import { renderProviderLogo } from '../../components/ProviderLogo';
import { renderPowerBy } from '../../components/PowerBy';
import { ENDPOINT } from '../../data/FormData';

/** One bookable time: the UTC instant to post back plus its visitor-tz label. */
interface Slot {
    at: string;
    label: string;
}

interface DaySlots {
    date: string;
    slots: Slot[];
}

interface ClinicInfo {
    provider_code: string;
    provider_name: string;
    duration_min: number;
}

/**
 * The call this caller already has, returned by a 409 on submit.
 *
 * Rescheduling is not offered here: this link is unauthenticated, so the page
 * shows the existing call and the CIPRA care team's contacts instead. A
 * clinician moves it from the dashboard, which notifies the patient.
 */
interface ExistingCall {
    at: string | null;
    isRegistered: boolean;
}

// Matches clinic_link_service.EXISTING_CALL_CODE.
const EXISTING_CALL_CODE = 'existing_call';

// The visitor's own timezone. An unregistered caller has no stored tz, so the
// browser's is the only sensible basis for displaying times.
const VISITOR_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

// Phone numbers are validated server-side as US (10 digits, optionally +1), so
// the input is locked to the US country code rather than offering a choice that
// would be rejected on submit.
const PHONE_COUNTRY = 'us';

// CIPRA's own care-team contacts. Deliberately NOT the provider's support_cell:
// the CIPRA care team places and reschedules these calls, so showing the
// client org's number (e.g. UCR) would send callers to a front desk that
// cannot see this schedule. Vanity letters are expanded for the tel: href
// (90-CIPRA -> 9024772), since a raw tel: with letters will not dial.
const SUPPORT_PHONE = '+1 (844) 90-CIPRA';
const SUPPORT_PHONE_TEL = '+18449024772';
const SUPPORT_EMAIL = 'support@cipra.ai';

/** "Mon" / "14" / "Mar" parts for a date chip, rendered in the visitor's tz. */
function chipParts(isoDate: string): { weekday: string; day: string; month: string } {
    // Parse as a local date (not UTC) so the chip matches the grouped day.
    const [y, m, d] = isoDate.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return {
        weekday: date.toLocaleDateString(undefined, { weekday: 'short' }),
        day: date.toLocaleDateString(undefined, { day: 'numeric' }),
        month: date.toLocaleDateString(undefined, { month: 'short' }),
    };
}

/** "Monday, March 14 at 2:30 PM" for the confirmation screen. */
function describeSlot(at: string): string {
    const date = new Date(at);
    const day = date.toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
    });
    const time = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return `${day} at ${time}`;
}

/**
 * Public per-provider call scheduling form (reached by QR code or SMS link).
 *
 * Mobile is the primary surface: a single scrolling page, a horizontal strip of
 * day chips, and a two-column time grid with touch-sized targets. Slots come
 * from the backend (the clinician pool's availability), never computed here.
 */
export function ScheduleCallPage(): JSX.Element {
    const { provider } = useParams();

    const [clinic, setClinic] = useState<ClinicInfo | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    const [days, setDays] = useState<DaySlots[]>([]);
    const [slotsMessage, setSlotsMessage] = useState<string | null>(null);
    const [slotsLoading, setSlotsLoading] = useState<boolean>(false);

    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [phone, setPhone] = useState<string>('');
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

    const [submitting, setSubmitting] = useState<boolean>(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [confirmed, setConfirmed] = useState<{ at: string; isRegistered: boolean } | null>(null);
    const [existingCall, setExistingCall] = useState<ExistingCall | null>(null);
    const [touched, setTouched] = useState<boolean>(false);

    // Validate the link itself before showing any form.
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        fetch(`${ENDPOINT}/clinic-call/${provider}`)
            .then(async (response) => {
                const body = await response.json().catch(() => ({}));
                if (cancelled) return;
                if (!response.ok) {
                    setLoadError(body.message ?? 'This scheduling link is not valid');
                    return;
                }
                setClinic(body as ClinicInfo);
            })
            .catch(() => {
                if (!cancelled) setLoadError('Unable to load scheduling. Please try again.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [provider]);

    // Load open slots once the link is known to be good.
    useEffect(() => {
        if (!clinic) return;
        let cancelled = false;
        setSlotsLoading(true);
        fetch(
            `${ENDPOINT}/clinic-call/${provider}/slots?tz=${encodeURIComponent(VISITOR_TZ)}`,
        )
            .then(async (response) => {
                const body = await response.json().catch(() => ({}));
                if (cancelled) return;
                if (!response.ok) {
                    setSlotsMessage(body.message ?? 'Unable to load available times');
                    return;
                }
                const loaded: DaySlots[] = body.days ?? [];
                setDays(loaded);
                setSlotsMessage(body.message ?? null);
                setSelectedDate(loaded.length > 0 ? loaded[0].date : null);
            })
            .catch(() => {
                if (!cancelled) setSlotsMessage('Unable to load available times');
            })
            .finally(() => {
                if (!cancelled) setSlotsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [clinic, provider]);

    const slotsForDay = useMemo(
        () => days.find((d) => d.date === selectedDate)?.slots ?? [],
        [days, selectedDate],
    );

    const validFirstName = firstName.trim().length > 0;
    const validLastName = lastName.trim().length > 0;
    // react-phone-input-2 yields digits only; a US number is 1 + 10 digits.
    const validPhone = phone.replace(/\D/g, '').length >= 11;
    const canSubmit = validFirstName && validLastName && validPhone && Boolean(selectedSlot);

    const submit = useCallback((): void => {
        setTouched(true);
        if (!canSubmit || submitting) return;
        setSubmitting(true);
        setFormError(null);
        fetch(`${ENDPOINT}/clinic-call/${provider}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                first_name: firstName.trim(),
                last_name: lastName.trim(),
                phone,
                start_at: selectedSlot,
                // Persisted server-side so SMS about this call (confirmation,
                // reschedule, reminders) state the time in the timezone the
                // caller actually booked in, not the clinic's.
                tz: VISITOR_TZ,
            }),
        })
            .then(async (response) => {
                const body = await response.json().catch(() => ({}));
                if (!response.ok) {
                    // 409: not a bad input, so show the call they already hold
                    // instead of an inline error under the form.
                    if (body.code === EXISTING_CALL_CODE) {
                        setExistingCall({
                            at: body.existing_call?.start_at_utc ?? null,
                            isRegistered: Boolean(body.is_registered),
                        });
                        return;
                    }
                    setFormError(body.message ?? 'Unable to schedule your call');
                    return;
                }
                setConfirmed({
                    at: body.start_at_utc ?? selectedSlot,
                    isRegistered: Boolean(body.is_registered),
                });
            })
            .catch(() => setFormError('Unable to schedule your call. Please try again.'))
            .finally(() => setSubmitting(false));
    }, [canSubmit, submitting, provider, firstName, lastName, phone, selectedSlot]);

    const shell = (children: JSX.Element) => (
        <div className="center-screen">
            <div
                className="glow-border page-container"
                style={{ maxWidth: '380px', width: '100%', boxSizing: 'border-box' }}
            >
                {children}
            </div>
        </div>
    );

    if (loading) {
        return shell(
            <div className="line-align-center">
                <Spinner size="large" />
            </div>,
        );
    }

    if (loadError || !clinic) {
        return shell(
            <SpaceBetween size="m" direction="vertical">
                <h2 className="line-align-center">Scheduling unavailable</h2>
                <p className="line-align-center">{loadError}</p>
            </SpaceBetween>,
        );
    }

    if (existingCall) {
        return shell(
            <SpaceBetween size="m" direction="vertical">
                {renderProviderLogo({ provider: clinic.provider_code })}
                <h2 className="line-align-center">You have an upcoming call</h2>
                {existingCall.at ? (
                    <p className="line-align-center">
                        You're scheduled for <b>{describeSlot(existingCall.at)}</b>.
                    </p>
                ) : (
                    <p className="line-align-center">
                        You have an upcoming call with the CIPRA Care Team.
                    </p>
                )}
                <p className="line-align-center">
                    Our team will call you within 10 minutes before or after your
                    scheduled time.
                </p>
                <p className="line-align-center">
                    Need a different time? Call{' '}
                    <a href={`tel:${SUPPORT_PHONE_TEL}`}>{SUPPORT_PHONE}</a> or email{' '}
                    <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>, and our care
                    team will be happy to reschedule.
                </p>
                {existingCall.isRegistered && (
                    <p className="line-align-center">
                        You can also reschedule or cancel it in your CIPRA app.
                    </p>
                )}
                {renderPowerBy({ provider: clinic.provider_code.toUpperCase() })}
            </SpaceBetween>,
        );
    }

    if (confirmed) {
        return shell(
            <SpaceBetween size="m" direction="vertical">
                {renderProviderLogo({ provider: clinic.provider_code })}
                <h2 className="line-align-center">You're scheduled</h2>
                <p className="line-align-center">
                    We'll call you on <b>{describeSlot(confirmed.at)}</b>.
                </p>
                <p className="line-align-center">
                    Our team will call you within 10 minutes before or after your
                    scheduled time.
                    {confirmed.isRegistered
                        ? " You'll also see it in your CIPRA app, where you can cancel or reschedule."
                        : ' The CIPRA Care Team will call the number you entered.'}
                </p>
                {renderPowerBy({ provider: clinic.provider_code.toUpperCase() })}
            </SpaceBetween>,
        );
    }

    return shell(
        <SpaceBetween size="m" direction="vertical">
            {renderProviderLogo({ provider: clinic.provider_code })}
            <h2 className="line-align-center">Schedule a call</h2>
            <p className="line-align-center">
                Pick a time and the CIPRA Care Team will call you.
            </p>

            <TextInput
                label="First Name"
                value={firstName}
                onChange={setFirstName}
                placeholder="First Name"
                showMessage={touched && !validFirstName}
            />
            <TextInput
                label="Last Name"
                value={lastName}
                onChange={setLastName}
                placeholder="Last Name"
                showMessage={touched && !validLastName}
            />

            <SpaceBetween size="m" direction="horizontal">
                <div>Phone Number</div>
            </SpaceBetween>
            <PhoneInput
                country={PHONE_COUNTRY}
                onlyCountries={[PHONE_COUNTRY]}
                disableDropdown
                value={phone}
                onChange={setPhone}
                inputProps={{ inputMode: 'tel', autoComplete: 'tel' }}
                buttonStyle={{
                    borderTopLeftRadius: '8px',
                    borderBottomLeftRadius: '8px',
                    borderTop: '2px solid #9ba7b6',
                    borderBottom: '2px solid #9ba7b6',
                    borderLeft: '2px solid #9ba7b6',
                }}
                inputStyle={{
                    width: '100%',
                    height: '44px',
                    borderTopRightRadius: '8px',
                    borderBottomRightRadius: '8px',
                    borderTop: '2px solid #9ba7b6',
                    borderBottom: '2px solid #9ba7b6',
                    borderRight: '2px solid #9ba7b6',
                }}
                placeholder="Phone number"
            />
            {touched && !validPhone && (
                <p className="tip-text">Please enter a valid phone number.</p>
            )}

            <SpaceBetween size="m" direction="horizontal">
                <div>Choose a time</div>
            </SpaceBetween>

            {slotsLoading && (
                <div className="line-align-center">
                    <Spinner />
                </div>
            )}

            {!slotsLoading && days.length === 0 && (
                <p className="tip-text">
                    {slotsMessage ?? 'No open times right now. Please try again later.'}
                </p>
            )}

            {!slotsLoading && days.length > 0 && (
                <>
                    {/* Day chips scroll horizontally so a month of options costs
                        one row of vertical space on a phone. */}
                    <div
                        style={{
                            display: 'flex',
                            gap: '8px',
                            overflowX: 'auto',
                            paddingBottom: '4px',
                            WebkitOverflowScrolling: 'touch',
                        }}
                    >
                        {days.map((day) => {
                            const { weekday, day: dayNum, month } = chipParts(day.date);
                            const active = day.date === selectedDate;
                            return (
                                <button
                                    key={day.date}
                                    type="button"
                                    onClick={() => {
                                        setSelectedDate(day.date);
                                        setSelectedSlot(null);
                                    }}
                                    style={{
                                        flex: '0 0 auto',
                                        minWidth: '56px',
                                        padding: '8px 4px',
                                        borderRadius: '8px',
                                        border: active
                                            ? '2px solid #0972d3'
                                            : '2px solid #9ba7b6',
                                        background: active ? '#f0f8ff' : '#ffffff',
                                        color: '#000716',
                                        cursor: 'pointer',
                                        textAlign: 'center',
                                        lineHeight: 1.3,
                                    }}
                                >
                                    <div style={{ fontSize: '11px' }}>{weekday}</div>
                                    <div style={{ fontSize: '17px', fontWeight: 700 }}>{dayNum}</div>
                                    <div style={{ fontSize: '11px' }}>{month}</div>
                                </button>
                            );
                        })}
                    </div>

                    {/* Two columns of 44px-tall targets: thumb-friendly, and two
                        times stay readable at 375px. */}
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '8px',
                        }}
                    >
                        {slotsForDay.map((slot) => {
                            const active = slot.at === selectedSlot;
                            return (
                                <button
                                    key={slot.at}
                                    type="button"
                                    onClick={() => setSelectedSlot(slot.at)}
                                    style={{
                                        minHeight: '44px',
                                        borderRadius: '8px',
                                        border: active
                                            ? '2px solid #0972d3'
                                            : '2px solid #9ba7b6',
                                        background: active ? '#f0f8ff' : '#ffffff',
                                        color: '#000716',
                                        fontSize: '15px',
                                        fontWeight: active ? 700 : 400,
                                        cursor: 'pointer',
                                    }}
                                >
                                    {slot.label}
                                </button>
                            );
                        })}
                    </div>

                    {/* Informational, not a validation error, so it must not use
                        the red .tip-text style the error messages share. */}
                    <p style={{ margin: 0, fontSize: '14px', color: '#5f6b7a' }}>
                        Times shown in your local time.
                    </p>
                    {touched && !selectedSlot && (
                        <p className="tip-text">Please choose a time.</p>
                    )}
                </>
            )}

            {formError && <p className="tip-text">{formError}</p>}

            <div className="line-align-center">
                <Button variant="primary" onClick={submit} disabled={submitting}>
                    {submitting && <Spinner />}
                    Schedule call
                </Button>
            </div>

            {renderPowerBy({ provider: clinic.provider_code.toUpperCase() })}
        </SpaceBetween>,
    );
}

export default ScheduleCallPage;
